/**
 * "Mein Zimmer" copy (COL-533).
 *
 * Same rules as `tenant-dashboard-v2-copy.ts`: pure data, both halves
 * key-for-key identical, so the six-language release picks these up instead of
 * finding English hardcoded in JSX.
 */

import { KEYNEST_DEFAULT_INSTRUCTIONS } from "@/lib/key-pickup";

export function getTenantRoomLabels(lang: "en" | "de") {
  return lang === "de"
    ? {
        contract: "Vertrag",
        contractPdf: "Mietvertrag als PDF",
        signNow: "Vertrag prüfen und unterschreiben",
        signHint: "Das Unterschreiben läuft noch über die bisherige Ansicht.",
        term: "Laufzeit",
        rentAllIn: "Miete warm",
        deposit: "Kaution",
        payments: "Zahlungen",
        paymentsPending:
          "Deine Zahlungen findest du vorerst im bisherigen Dashboard.",
        moveInOut: "Ein- & Auszug",
        infoDocs: "Infos & Dokumente",
        address: "Adresse",
        room: "Zimmer",
        floorLine: "Etage {floor}",
        contractRowTitle: "Mietvertrag",
        contractSignedOn: "Unterschrieben am {date} · Version {version}",
        contractVersionOnly: "Version {version}",
        contractPdfPill: "PDF",
        // Shown INSTEAD of the PDF pill when no openable document exists yet.
        contractPdfPending: "Noch nicht verfügbar",
        landlordConfirmation:
          "Vermieterbescheinigung (Wohnungsgeberbestätigung)",
        landlordConfirmationHint: "Für die Anmeldung beim Bürgeramt",
        landlordConfirmationPending:
          "Bekommst du am Einzugstag, für die Anmeldung beim Bürgeramt",
        fromMoveIn: "Ab Einzug",
        moveInReport: "Einzugsprotokoll",
        moveOutReport: "Auszugsprotokoll",
        moveInLocked: "Einzug {date} · freigeschaltet ab {from}",
        moveInReady:
          "Einzug {date} · dokumentiere dein Zimmer und die Gemeinschaftsflächen mit Fotos, ca. 10 Min.",
        moveInDone: "Einzug {date} · eingereicht, Zustand dokumentiert",
        moveInExpired:
          "Einzug {date} · Frist abgelaufen, Zimmer gilt als angenommen",
        moveOutLocked: "Auszug {date} · freigeschaltet ab {from}",
        // No confirmed move-out: the booking still carries its contract end
        // date, which must never be presented as a move-out date.
        moveOutNotScheduled: "Kein Auszug geplant",
        // Checkout protocol (tenant-requested move-out date).
        requestMoveOutDate: "Auszugsdatum beantragen",
        requestMoveOutDateRowHint:
          "Noch kein Auszug geplant · du kannst hier dein Datum beantragen",
        requestMoveOutDateEarliest: "Frühestens laut Vertrag: {date}",
        requestMoveOutDatePopoverLabel: "Auszug",
        requestMoveOutDateSubmit: "Zur Genehmigung einreichen",
        requestMoveOutDateSuccess: "Auszugsdatum beantragt",
        requestMoveOutDateError: "Dein Antrag konnte nicht gesendet werden",
        requestMoveOutDateAwaitingPill: "Wartet auf Genehmigung",
        requestMoveOutDateRequestedNote:
          "Auszug beantragt für {date} · gesendet am {sentDate}, wir bestätigen per E-Mail",
        // Key pickup / return, consolidated out of the old check-in screen.
        keyPickupLabel: "Schlüsselabholung",
        keyPickupUnlocksAt: "Freischaltung {datetime} · Berliner Zeit",
        keyPickupLockedHint:
          "Abholort und Zugangscode erscheinen am Tag vor deinem Einzug.",
        // Loading and failure are NOT "not arranged yet" -- that is an answer,
        // and we only have one once the query returns.
        keyPickupLoading: "Abholdetails werden geladen …",
        keyPickupFailed:
          "Die Abholdetails konnten nicht geladen werden. Lade die Seite neu.",
        keyPickupPending: "Noch nicht hinterlegt",
        keyPickupPendingHint:
          "Wir ergänzen die Abholdetails rechtzeitig vor deinem Einzug.",
        keyPickupOpenLink: "KeyNest öffnen",
        keyLocker: "Schließfach",
        keyCode: "Code",
        keyAccessCode: "Zugangscode",
        keyNestDefaultInstructions: KEYNEST_DEFAULT_INSTRUCTIONS.de,
        keyReturnLabel: "Schlüsselrückgabe",
        keyReturnNotYet: "Noch nicht geplant",
        keyReturnNotYetHint:
          "Die Rückgabedetails erscheinen hier, sobald dein Auszugsdatum feststeht.",
        keyReturnScheduled: "Rückgabe am Auszugstag",
        keyReturnDefaultHint:
          "Gib die Schlüssel am selben Ort zurück, an dem du sie abgeholt hast.",
        // DocuSign return hop.
        signedSuccessfully:
          "Vertrag unterschrieben. Dein unterschriebener Vertrag steht unten.",
        signingSyncFailed:
          "Status konnte nicht aktualisiert werden. Deine Unterschrift ist bei DocuSign gespeichert.",
        signingSessionExpired:
          "Die Signatur-Sitzung ist abgelaufen. Starte sie bitte neu.",
        // Stripe return hop.
        paymentSuccessful:
          "Zahlung erfolgreich. Deine Zahlung ist eingegangen.",
        paymentCanceled: "Zahlung abgebrochen. Es wurde nichts abgebucht.",
        moveOutReady:
          "Auszug {date} · dokumentiere den Zustand mit Fotos, ca. 10 Min.",
        moveOutExpired: "Auszug {date} · Frist abgelaufen",
        moveOutClosed: "Auszug {date} · von uns geschlossen",
        moveInClosed: "Einzug {date} · von uns geschlossen",
        moveOutDone: "Auszug {date} · eingereicht, Zustand dokumentiert",
        startReport: "Protokoll starten",
        resumeReport: "Weiter ausfüllen",
        reportPdf: "Protokoll-PDF",
        readConfirm: "Ich habe den Vertrag und die Hausordnung gelesen",
        signDigitally: "Digital unterschreiben",
        openingDocusign: "DocuSign wird geöffnet …",
        openingThisTab: "Wird in diesem Tab geöffnet.",
        confirmSignTitle: "Dein Vertrag ist bereit zum Unterschreiben",
        confirmSignBody:
          "Das öffnet die offizielle DocuSign-Sitzung. Schließe sie dort ab — wir bringen dich danach hierher zurück.",
        confirmSignCta: "Verstanden, weiter zu DocuSign",
        cancel: "Abbrechen",
        dobTitle: "Bestätige dein Geburtsdatum",
        dobBody:
          "Es steht in deinem Mietvertrag, deshalb prüfen wir es einmal vor der Unterschrift.",
        dobLabel: "Geburtsdatum",
        dobSave: "Weiter",
        dobEmpty: "Bitte gib dein Geburtsdatum ein.",
        dobInvalid: "Bitte gib ein gültiges Geburtsdatum ein.",
        dobUnderage:
          "Du musst mindestens 18 Jahre alt sein, um den Mietvertrag zu unterschreiben.",
        signFailed: "Die Unterschrift konnte nicht gestartet werden.",
        wasteSortingDoc: "Mülltrennung",
        wasteSortingDocHint: "So trennst du deinen Müll",
        broadcastingFeeDoc: "Rundfunkbeitrag (zahlt coliviq)",
        broadcastingFeeDocHint: "Was tun bei Post vom Beitragsservice",
        documentUnavailable:
          "Dieses Dokument lässt sich gerade nicht öffnen. Bitte versuche es später noch einmal.",
      }
    : {
        contract: "Contract",
        contractPdf: "Rental contract as PDF",
        signNow: "Review and sign your contract",
        signHint: "Signing still happens in the previous view.",
        term: "Term",
        rentAllIn: "Rent (all-in)",
        deposit: "Deposit",
        payments: "Payments",
        paymentsPending:
          "Your payments are still on the previous dashboard for now.",
        moveInOut: "Move-in & move-out",
        infoDocs: "Info & documents",
        address: "Address",
        room: "Room",
        floorLine: "Floor {floor}",
        contractRowTitle: "Rental contract",
        contractSignedOn: "Signed on {date} \u00b7 Version {version}",
        contractVersionOnly: "Version {version}",
        contractPdfPill: "PDF",
        contractPdfPending: "Not available yet",
        landlordConfirmation:
          "Landlord confirmation (Wohnungsgeberbest\u00e4tigung)",
        landlordConfirmationHint: "For registering at the B\u00fcrgeramt",
        landlordConfirmationPending:
          "You get it on move-in day, for registering at the B\u00fcrgeramt",
        fromMoveIn: "From move-in",
        moveInReport: "Move-in handover report",
        moveOutReport: "Move-out handover report",
        moveInLocked: "Move-in {date} \u00b7 unlocked from {from}",
        moveInReady:
          "Move-in {date} \u00b7 document your room and shared spaces with photos, approx. 10 min",
        moveInDone: "Move-in {date} \u00b7 submitted, condition documented",
        moveInExpired:
          "Move-in {date} \u00b7 the window closed, the room is deemed accepted",
        moveOutLocked: "Move-out {date} \u00b7 unlocked from {from}",
        moveOutNotScheduled: "No move-out scheduled",
        // Checkout protocol (tenant-requested move-out date).
        requestMoveOutDate: "Request check-out date",
        requestMoveOutDateRowHint:
          "No move-out scheduled yet \u00b7 you can request your date here",
        requestMoveOutDateEarliest: "Earliest per contract: {date}",
        requestMoveOutDatePopoverLabel: "Move-out",
        requestMoveOutDateSubmit: "Submit for approval",
        requestMoveOutDateSuccess: "Check-out date requested",
        requestMoveOutDateError: "Couldn't submit your request",
        requestMoveOutDateAwaitingPill: "Awaiting approval",
        requestMoveOutDateRequestedNote:
          "Requested move-out {date} \u00b7 sent {sentDate}, we'll confirm by email",
        keyPickupLabel: "Key pickup",
        keyPickupUnlocksAt: "Unlocks {datetime} \u00b7 Berlin time",
        keyPickupLockedHint:
          "Pickup location and access code appear the day before your move-in.",
        keyPickupLoading: "Loading pickup details …",
        keyPickupFailed:
          "Pickup details could not be loaded. Please reload the page.",
        keyPickupPending: "Not set up yet",
        keyPickupPendingHint:
          "We add the pickup details in good time before your move-in.",
        keyPickupOpenLink: "Open KeyNest",
        keyLocker: "Locker",
        keyCode: "Code",
        keyAccessCode: "Access code",
        keyNestDefaultInstructions: KEYNEST_DEFAULT_INSTRUCTIONS.en,
        keyReturnLabel: "Key return",
        keyReturnNotYet: "Not scheduled yet",
        keyReturnNotYetHint:
          "Return details appear here once your move-out date is confirmed.",
        keyReturnScheduled: "Return on your move-out day",
        keyReturnDefaultHint:
          "Return the keys to the same place you collected them.",
        signedSuccessfully:
          "Contract signed. Your signed contract is shown below.",
        signingSyncFailed:
          "Could not update the contract status. Your signature is recorded in DocuSign.",
        signingSessionExpired:
          "The signing session expired. Please start it again.",
        paymentSuccessful:
          "Payment successful. Your payment has been recorded.",
        paymentCanceled: "Payment canceled. You were not charged.",
        moveOutReady:
          "Move-out {date} \u00b7 document the condition with photos, approx. 10 min",
        moveOutExpired: "Move-out {date} \u00b7 the window has closed",
        moveOutClosed: "Move-out {date} \u00b7 closed by us",
        moveInClosed: "Move-in {date} \u00b7 closed by us",
        moveOutDone: "Move-out {date} \u00b7 submitted, condition documented",
        startReport: "Start report",
        resumeReport: "Continue report",
        reportPdf: "Report PDF",
        readConfirm: "I have read the contract and house rules",
        signDigitally: "Sign digitally",
        openingDocusign: "Opening DocuSign\u2026",
        openingThisTab: "Opening in this tab.",
        confirmSignTitle: "Your contract is ready to sign",
        confirmSignBody:
          "This opens the official DocuSign session. Finish it there \u2014 we\u2019ll bring you back here afterwards.",
        confirmSignCta: "I understand, continue to DocuSign",
        cancel: "Cancel",
        dobTitle: "Confirm your date of birth",
        dobBody:
          "It appears on your rental contract, so we check it once before you sign.",
        dobLabel: "Date of birth",
        dobSave: "Continue",
        dobEmpty: "Please enter your date of birth.",
        dobInvalid: "Please enter a valid date of birth.",
        dobUnderage:
          "You must be at least 18 years old to sign the rental contract.",
        signFailed: "Could not start signing.",
        wasteSortingDoc: "Waste sorting",
        wasteSortingDocHint: "How to sort your waste",
        broadcastingFeeDoc: "Broadcasting fee (paid by coliviq)",
        broadcastingFeeDocHint:
          "What to do if you get a letter from the Beitragsservice",
        documentUnavailable:
          "That document can\u2019t be opened right now. Please try again later.",
      };
}
